import moment from "moment";
import { Subject } from "rxjs";
import { Show } from "../../domain/show";

export abstract class CalendarView {
  private _currentDate: moment.Moment;

  protected constructor(
    currentDate: moment.Moment,
    private _daysCount: number,
    private _dayPartDuration: moment.Duration,
    private _viewDuration: moment.Duration,
    private _dayPartFormat: string,
    private _videoTitleTimeFormat: string,
  ) {
    this._currentDate = currentDate.clone();
  }

  get currentDate() {
    return this._currentDate.clone();
  }

  get daysCount() {
    return this._daysCount;
  }

  get dayPartDuration() {
    return this._dayPartDuration;
  }

  get viewDuration() {
    return this._viewDuration;
  }

  get videoTitleTimeFormat() {
    return this._videoTitleTimeFormat;
  }

  createDayParts(): Array<any> {
    const dayParts = [];
    const dayPart = this.fromDate();
    const toDate =
      this.daysCount > 1 ? this.fromDate().add(1, "days") : this.toDate();
    while (dayPart <= toDate) {
      dayParts.push({
        text: this.formatDayPart(dayPart),
        id: dayPart.valueOf(),
      });
      dayPart.add(this.dayPartDuration);
    }
    return dayParts;
  }

  formatDates(): Array<string> {
    const dates = [];
    const date = this.fromDate();
    for (let i = 0; i < this._daysCount; i++) {
      dates.push(date.format("ddd D/M"));
      date.add(1, "days");
    }
    return dates;
  }

  formatDayPart(dayPart: moment.Moment): string {
    return dayPart.format(this._dayPartFormat);
  }

  toDate(): moment.Moment {
    return this.fromDate().add(this.viewDuration);
  }

  abstract prev(): CalendarView;

  abstract next(): CalendarView;

  clone(): CalendarView {
    return this.setCurrentDate(this.currentDate);
  }

  abstract setCurrentDate(currentDate: moment.Moment): CalendarView;

  abstract fromDate(): moment.Moment;

  abstract title(): string;

  onMouseWheelEventHandler(event: WheelEvent): CalendarView {
    console.log("onMouseWheelEventHandler, wheelDeltaY: ", event.deltaY);
    if (event.deltaY > 10) {
      return this.next();
    }

    if (event.deltaY < -10) {
      return this.prev();
    }

    return this;
  }

  onDragOverHandler(event: DragEvent, selectedShow: Show): CalendarView {
    if (
      this.fromDate().valueOf() >= selectedShow.startTime.valueOf() &&
      this.fromDate().valueOf() <= selectedShow.endTime.valueOf()
    ) {
      return this.prev();
    }
    if (
      this.toDate().valueOf() >= selectedShow.startTime.valueOf() &&
      this.toDate().valueOf() <= selectedShow.endTime.valueOf()
    ) {
      return this.next();
    }

    return this;
  }
}

export class WeekCalendarView extends CalendarView {
  constructor(currentDate: moment.Moment) {
    super(
      currentDate,
      7,
      moment.duration(1, "hours"),
      moment.duration(7, "days"),
      "HH:mm",
      "HH:mm",
    );
  }

  onMouseWheelEventHandler(event): CalendarView {
    return this.setCurrentDate(this.currentDate);
  }

  onDragOverHandler(event: DragEvent, selectedShow: Show): CalendarView {
    return this.setCurrentDate(this.currentDate);
  }

  prev() {
    return new WeekCalendarView(this.currentDate.subtract(7, "days"));
  }

  next() {
    return new WeekCalendarView(this.currentDate.add(7, "days"));
  }

  setCurrentDate(currentDate) {
    return new WeekCalendarView(currentDate);
  }

  fromDate(): moment.Moment {
    return this.currentDate.startOf("week");
  }

  title() {
    return (
      this.fromDate().format("MMM D") +
      " - " +
      this.fromDate().add(6, "days").format("MMM D")
    );
  }
}

export class DayCalendarView extends CalendarView {
  constructor(currentDate: moment.Moment) {
    super(
      currentDate,
      1,
      moment.duration(1, "hours"),
      moment.duration(1, "days"),
      "HH:mm",
      "HH:mm:ss:SSS",
    );
  }

  onMouseWheelEventHandler(event): CalendarView {
    return this.setCurrentDate(this.currentDate);
  }

  onDragOverHandler(event: DragEvent, selectedShow: Show): CalendarView {
    return this.setCurrentDate(this.currentDate);
  }

  prev() {
    return new DayCalendarView(this.currentDate.subtract(1, "days"));
  }

  next() {
    return new DayCalendarView(this.currentDate.add(1, "days"));
  }

  setCurrentDate(currentDate) {
    return new DayCalendarView(currentDate);
  }

  fromDate() {
    return this.currentDate.startOf("day");
  }

  title() {
    return this.currentDate.format("dddd D/M");
  }
}

export class FiveMinCalendarView extends CalendarView {
  constructor(currentDate: moment.Moment) {
    super(
      currentDate,
      1,
      moment.duration(30, "seconds"),
      moment.duration(5, "minutes"),
      "HH:mm:ss",
      "HH:mm:ss:SSS",
    );
  }

  prev() {
    return new FiveMinCalendarView(this.currentDate.subtract(5, "minutes"));
  }

  next() {
    return new FiveMinCalendarView(this.currentDate.add(5, "minutes"));
  }

  setCurrentDate(currentDate) {
    return new FiveMinCalendarView(currentDate);
  }

  fromDate() {
    return this.currentDate
      .minutes(this.currentDate.minutes() - (this.currentDate.minutes() % 5))
      .seconds(0)
      .milliseconds(0);
  }

  title() {
    return (
      this.currentDate.format("dddd D/M") +
      "\xa0\xa0\xa0" +
      this.fromDate().format("H:mm") +
      " - " +
      this.fromDate().add("5", "minutes").format("H:mm")
    );
  }
}

export class HourCalendarView extends CalendarView {
  constructor(currentDate: moment.Moment) {
    super(
      currentDate,
      1,
      moment.duration(5, "minutes"),
      moment.duration(1, "hours"),
      "HH:mm",
      "HH:mm:ss:SSS",
    );
  }

  prev() {
    return new HourCalendarView(this.currentDate.subtract(1, "hours"));
  }

  next() {
    return new HourCalendarView(this.currentDate.add(1, "hours"));
  }

  setCurrentDate(currentDate) {
    return new HourCalendarView(currentDate);
  }

  fromDate() {
    return this.currentDate.startOf("hour");
  }

  title() {
    return (
      this.currentDate.format("dddd D/M") +
      "\xa0\xa0\xa0" +
      this.fromDate().format("H:00") +
      " - " +
      this.fromDate().add("1", "hours").format("H:00")
    );
  }
}
